
import React, { useMemo } from 'react';
import {
  TextField,
  MenuItem,
  Grid,
  Typography,
  InputAdornment,
} from '@mui/material';
import { CalendarToday } from '@mui/icons-material';
import { useUsers } from '../../hooks/user/useUsers';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import './AssignmentForm.css';

const AssignmentForm = ({
  formData,
  onChange,
  errors = {},
  disabled = false,
  currentUser,
  currentUserRole = 'client_admin',
}) => {
  const targetRoleLabel =
    currentUserRole === 'client_admin' ? 'Project Admin' : 'Surveyor';

  const filters = useMemo(
    () => ({
      search: '',
      group__name:
        targetRoleLabel === 'Project Admin' ? 'project_admin' : 'surveyor',
      is_active: true,
    }),
    [targetRoleLabel]
  );

  const { users, loading: usersLoading, error: usersError } = useUsers(
    filters,
    1,
    1000,
    currentUser,
    true
  );

  const hasNoUsers = !usersLoading && users.length === 0;

  const commonTextFieldSx = {
    '& .MuiOutlinedInput-root': {
      fontSize: '14px',
      borderRadius: '8px',
      backgroundColor: disabled ? '#f9fafb' : '#fff',
    },
    '& .MuiOutlinedInput-input': {
      padding: '10px 14px',
    },
  };

  const dateFieldSx = {
    ...commonTextFieldSx,
    '& input': {
      paddingLeft: '40px',
    },
  };

  return (
    <div className="assignment-form">
      <Grid container spacing={3}>
        {/* ───────── ROW 1 ───────── */}
        <Grid item xs={12} md={3}>
          <Typography className="form-label">
            Select {targetRoleLabel}
            <span className="required-asterisk">*</span>
          </Typography>

          <TextField
            select
            fullWidth
            name="user"
            value={formData.user || ''}
            onChange={onChange}
            disabled={disabled || usersLoading}
            error={!!errors.user || !!usersError}
            helperText={
              usersError
                ? 'Failed to load users'
                : errors.user ||
                (usersLoading
                  ? 'Loading users...'
                  : hasNoUsers
                    ? `No ${targetRoleLabel.toLowerCase()}s available`
                    : undefined)
            }
            SelectProps={{
              displayEmpty: true,
              renderValue: (selected) =>
                selected ? (
                  users.find((u) => u.id === selected)?.full_name ||
                  users.find((u) => u.id === selected)?.profile?.full_name
                ) : (
                  <span style={{ color: '#9ca3af' }}>
                    Choose {targetRoleLabel}...
                  </span>
                ),
            }}
            sx={commonTextFieldSx}
          >
            <MenuItem value="" disabled>
              Choose {targetRoleLabel}...
            </MenuItem>

            {users.map((user) => (
              <MenuItem key={user.id} value={user.id}>
                {user.full_name || user.profile?.full_name}
              </MenuItem>
            ))}
          </TextField>
        </Grid>

        <Grid item xs={12} md={3}>
          <Typography className="form-label">
            Access Level
            <span className="required-asterisk">*</span>
          </Typography>

          <TextField
            select
            fullWidth
            name="access_level"
            value={formData.access_level || 'read_write'}
            onChange={onChange}
            error={!!errors.access_level}
            helperText={errors.access_level}
            sx={commonTextFieldSx}
          >
            <MenuItem value="read_only">Read Only</MenuItem>
            <MenuItem value="read_write">Read & Write</MenuItem>
            <MenuItem value="full_access">Full Access</MenuItem>
          </TextField>
        </Grid>

        <Grid item xs={12} md={3}>
          <Typography className="form-label">Start Date</Typography>

          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              label="Start Date"
              value={formData.start_date ? dayjs(formData.start_date) : null}
              onChange={(newValue) => {
                onChange({
                  target: {
                    name: 'start_date',
                    value: newValue ? newValue.format('YYYY-MM-DD') : '',
                  },
                });
              }}
              slotProps={{
                textField: {
                  fullWidth: true,
                  sx: dateFieldSx,
                },
              }}
            />
          </LocalizationProvider>

        </Grid>

        <Grid item xs={12} md={3}>
          <Typography className="form-label">End Date</Typography>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              label="End Date"
              value={formData.end_date ? dayjs(formData.end_date) : null}
              onChange={(newValue) => {
                onChange({
                  target: {
                    name: 'end_date',
                    value: newValue ? newValue.format('YYYY-MM-DD') : '',
                  },
                });
              }}
              slotProps={{
                textField: {
                  fullWidth: true,
                  sx: dateFieldSx,
                },
              }}
            />
          </LocalizationProvider>


        </Grid>

        <Grid item xs={12} md={6}>
          <Typography className="form-label">
            Remark / Note
          </Typography>

          <TextField
            fullWidth
            multiline
            rows={3}
            name="remark"
            value={formData.remark || ''}
            onChange={onChange}
            sx={{
              ...commonTextFieldSx,
              '& .MuiOutlinedInput-root': { padding: 0 },
            }}
          />
        </Grid>
      </Grid>
    </div>
  );
};

export default AssignmentForm;
